import {
  bracedName,
  compareNames,
  createNode,
  rvtElementName,
  sortNodes,
  specialNode,
} from './tree-node';
import type {
  DrawablesMap,
  ExplorerSectionBox,
  ExplorerSectionPlane,
  ExplorerTab,
  ExplorerTreeLabels,
  ExplorerTreeNode,
  ExplorerViewPoint,
  RvtBox,
  RvtCategory,
  RvtElement,
  RvtTransform,
} from './types';

/** Scene Entities an element draws as. Falls back to the raw Id for pre-`appearances` metadata. */
export function elementAppearances(element: RvtElement): string[] {
  return element.appearances?.length ? element.appearances : [String(element.Id)];
}

type CategoryChainElement = Pick<RvtElement, 'Type' | 'Family' | 'Category'>;

/**
 * Revit category an element belongs to, e.g. "Walls". Only the Family/Type at the top of an
 * element's chain carries `Category`, so instances have to walk up to reach it.
 */
export function rvtCategoryName(
  elements: CategoryChainElement[],
  categories: RvtCategory[],
  element: CategoryChainElement,
): string {
  const seen = new Set<CategoryChainElement>();
  let current: CategoryChainElement | undefined = element;

  while (current && !seen.has(current)) {
    seen.add(current);
    if (current.Category != null) return categories[current.Category]?.Name ?? '';
    const parentIndex = current.Type ?? current.Family ?? null;
    current = parentIndex == null ? undefined : elements[parentIndex];
  }

  return '';
}

export function buildRvtTab(
  elements: RvtElement[],
  categories: RvtCategory[],
  drawables: DrawablesMap,
  tab: ExplorerTab,
  labels: ExplorerTreeLabels,
): ExplorerTreeNode[] {
  const getDrawables = (element: RvtElement) =>
    elementAppearances(element)
      .map((appearance) => drawables[appearance])
      .filter(Boolean);

  const elementNode = (index: number, children: ExplorerTreeNode[] = []) => {
    const element = elements[index];
    const [drawable, ...extra] = getDrawables(element);
    const node = createNode(
      String(index),
      rvtElementName(element),
      drawable ? String(drawable.id) : undefined,
      children,
      String(element.Id),
    );
    for (const entity of extra) node.objectIds.push(String(entity.id));
    return node;
  };

  // Keys mix two index spaces: `e<i>` indexes Elements, `c<i>` indexes Categories.
  type NodeMap = Map<string, NodeMap>;

  const childMapOf = (map: NodeMap, key: string): NodeMap => {
    let childMap = map.get(key);
    if (!childMap) {
      childMap = new Map();
      map.set(key, childMap);
    }
    return childMap;
  };

  const categoryHierarchy = (filter: (element: RvtElement) => boolean): ExplorerTreeNode[] => {
    const roots: NodeMap = new Map();

    const walk = (index: number, chain: Set<number>): NodeMap => {
      chain.add(index);
      const element = elements[index];
      const parentIndex = element.Type ?? element.Family ?? null;
      const categoryIndex = element.Category ?? null;
      const parentMap =
        parentIndex !== null && elements[parentIndex] && !chain.has(parentIndex)
          ? walk(parentIndex, chain)
          : categoryIndex !== null && categories[categoryIndex]
            ? childMapOf(roots, `c${categoryIndex}`)
            : roots;

      return childMapOf(parentMap, `e${index}`);
    };

    elements.forEach((element, index) => {
      if (getDrawables(element).length > 0 && filter(element)) walk(index, new Set());
    });

    const toNodes = (map: NodeMap): ExplorerTreeNode[] =>
      sortNodes(
        [...map.entries()].map(([key, childrenMap]) => {
          const index = Number(key.slice(1));
          const children = toNodes(childrenMap);
          return key.startsWith('c')
            ? createNode(key, categories[index].Name || key, undefined, children)
            : elementNode(index, children);
        }),
      );

    return toNodes(roots);
  };

  if (tab === 'classes') {
    return categoryHierarchy(() => true);
  }

  const levels = elements
    .map((element, index) => ({ element, index }))
    .filter(({ element }) => element.class === 'Level')
    .map(({ element, index }) => {
      const node = createNode(
        `level-${index}`,
        rvtElementName(element),
        undefined,
        categoryHierarchy((candidate) => candidate.Level === index),
        String(element.Id),
      );
      if (element.Elevation !== undefined) node.elevation = element.Elevation;
      return node;
    })
    .filter((node) => node.children.length > 0);

  sortNodes(levels);

  const noLevel = categoryHierarchy((candidate) => (candidate.Level ?? null) === null);
  if (noLevel.length > 0) {
    levels.push(createNode('no-level', bracedName(labels.noLevel), undefined, noLevel));
  }

  return levels;
}

export function buildRvtViews(
  elements: RvtElement[],
  labels: ExplorerTreeLabels,
  worldMatrix?: number[],
): ExplorerTreeNode | undefined {
  const byDiscipline: Record<string, ExplorerTreeNode[]> = {};
  const noDiscipline: ExplorerTreeNode[] = [];

  elements.forEach((element, index) => {
    if (element.class !== 'View3D' || element.IsTemplate) return;
    const view = buildViewPoint(element, worldMatrix);
    if (!view) return;

    const node = createNode(
      `view-${index}`,
      rvtElementName(element),
      undefined,
      [],
      String(element.Id),
      view,
    );
    if (element.Discipline) (byDiscipline[element.Discipline] ||= []).push(node);
    else noDiscipline.push(node);
  });

  const disciplines = Object.keys(byDiscipline)
    .sort(compareNames)
    .map((discipline) =>
      createNode(
        `discipline-${discipline}`,
        discipline,
        undefined,
        sortNodes(byDiscipline[discipline]),
      ),
    );

  const children = [...disciplines, ...sortNodes(noDiscipline)];
  if (children.length === 0) return undefined;

  return specialNode('views-3d', labels.views3d, children);
}

const ZUP_TO_YUP = [1, 0, 0, 0, 0, 0, -1, 0, 0, 1, 0, 0, 0, 0, 0, 1];

// The converter's GLB root scales feet to metres (RVT_Root matrix), and the XKT inherits it.
export const REVIT_FEET_TO_MODEL_METRES = 0.3048;

const scaleMat4 = (scale: number) => [scale, 0, 0, 0, 0, scale, 0, 0, 0, 0, scale, 0, 0, 0, 0, 1];

const REVIT_TO_MODEL = mulMat4(scaleMat4(REVIT_FEET_TO_MODEL_METRES), ZUP_TO_YUP);

function compileTransform(transform: RvtTransform): number[] {
  const matrix = new Array(16).fill(0);
  for (let i = 0; i < 3; i++) {
    matrix[i] = transform.Basis[0][i];
    matrix[4 + i] = transform.Basis[1][i];
    matrix[8 + i] = transform.Basis[2][i];
    matrix[12 + i] = transform.Origin[i];
  }
  matrix[15] = 1;
  return matrix;
}

function mulMat4(a: number[], b: number[]): number[] {
  const result = new Array(16).fill(0);
  for (let column = 0; column < 4; column++) {
    for (let row = 0; row < 4; row++) {
      let sum = 0;
      for (let k = 0; k < 4; k++) sum += a[k * 4 + row] * b[column * 4 + k];
      result[column * 4 + row] = sum;
    }
  }
  return result;
}

function transformPoint3(m: number[], p: number[]): number[] {
  return [
    m[0] * p[0] + m[4] * p[1] + m[8] * p[2] + m[12],
    m[1] * p[0] + m[5] * p[1] + m[9] * p[2] + m[13],
    m[2] * p[0] + m[6] * p[1] + m[10] * p[2] + m[14],
  ];
}

function transformVec3(m: number[], v: number[]): number[] {
  return [
    m[0] * v[0] + m[4] * v[1] + m[8] * v[2],
    m[1] * v[0] + m[5] * v[1] + m[9] * v[2],
    m[2] * v[0] + m[6] * v[1] + m[10] * v[2],
  ];
}

function normalizeVec3(v: number[]): number[] {
  const length = Math.hypot(v[0], v[1], v[2]);
  return length > 0 ? v.map((value) => value / length) : v;
}

function hasBoxGeometry(box: RvtBox | undefined): box is RvtBox {
  return Boolean(box?.Transform?.Basis && box.Transform.Origin && box.Min && box.Max);
}

function buildSectionBox(
  element: RvtElement,
  worldMatrix?: number[],
): ExplorerSectionPlane[] | undefined {
  const sectionBox = element.SectionBox;
  if (!element.IsSectionBoxActive || !hasBoxGeometry(sectionBox)) return undefined;

  const modelTransform = mulMat4(REVIT_TO_MODEL, compileTransform(sectionBox.Transform));
  const transform = worldMatrix ? mulMat4(worldMatrix, modelTransform) : modelTransform;
  const center = [0, 1, 2].map((i) => (sectionBox.Min[i] + sectionBox.Max[i]) * 0.5);
  const halfDiagonal = [0, 1, 2].map((i) => sectionBox.Max[i] - center[i]);

  return [0, 1, 2].flatMap((axis) =>
    [1, -1].map((sign) => {
      const normal = [0, 0, 0];
      normal[axis] = sign;
      const face = [0, 1, 2].map((i) => center[i] + normal[i] * halfDiagonal[i]);

      return {
        pos: transformPoint3(transform, face),
        dir: normalizeVec3(
          transformVec3(
            transform,
            normal.map((value) => -value),
          ),
        ),
      };
    }),
  );
}

export function buildRvtSectionBoxes(
  elements: RvtElement[],
  worldMatrix?: number[],
): ExplorerSectionBox[] {
  return elements.flatMap((element) => {
    if (element.class !== 'View3D' || element.IsTemplate) return [];

    const planes = buildSectionBox(element, worldMatrix);
    if (!planes) return [];

    return [{ id: String(element.Id), name: rvtElementName(element), planes }];
  });
}

// Revit hands us a degenerate crop box for views that never had one sized, so anything
// this thin is treated as "no crop box" rather than scaled into a pinhole view.
const MIN_USABLE_CROP_WIDTH_METRES = 1;

function buildOrthoScale(element: RvtElement): number | undefined {
  const cropBox = element.CropBox;
  if (!element.CropBoxActive || !hasBoxGeometry(cropBox)) return undefined;

  const width = (cropBox.Max[0] - cropBox.Min[0]) * REVIT_FEET_TO_MODEL_METRES;
  return width >= MIN_USABLE_CROP_WIDTH_METRES ? 2 * width : undefined;
}

function buildViewPoint(
  element: RvtElement,
  worldMatrix?: number[],
): ExplorerViewPoint | undefined {
  const orientation = element.Orientation;
  if (!orientation) return undefined;

  const transform = worldMatrix ? mulMat4(worldMatrix, REVIT_TO_MODEL) : REVIT_TO_MODEL;
  const eye = transformPoint3(transform, orientation.EyePosition);
  const forward = normalizeVec3(transformVec3(transform, orientation.ForwardDirection));

  return {
    eye,
    look: [0, 1, 2].map((axis) => eye[axis] + forward[axis]),
    up: normalizeVec3(transformVec3(transform, orientation.UpDirection)),
    projection: element.IsPerspective ? 'perspective' : 'ortho',
    orthoScale: buildOrthoScale(element),
  };
}
